import { rateForZone, type RateZone } from "./calibration";

export type SpeechSegmentKind =
  | "chunk"
  | "item"
  | "paragraph-heading"
  | "subparagraph-heading";

export interface SpeechSegment {
  readonly id: string;
  readonly kind: SpeechSegmentKind;
  readonly text: string;
  readonly zone: RateZone;
}

export interface ScheduledSpeech {
  readonly gapAfterMs: number;
  readonly gapBeforeMs: number;
  readonly id: string;
  readonly kind: SpeechSegmentKind;
  readonly markerBefore: boolean;
  readonly rate: number;
  readonly text: string;
  readonly zone: RateZone;
}

export interface ScheduleSettings {
  readonly baseMultiplier: number;
  readonly repeatMission?: boolean;
}

const BEFORE_GAPS: Readonly<Record<SpeechSegmentKind, number>> = Object.freeze({
  "paragraph-heading": 2500,
  "subparagraph-heading": 1500,
  item: 900,
  chunk: 400,
});

const HEADING_AFTER_GAPS: Readonly<Record<string, number>> = Object.freeze({
  "paragraph-heading": 1200,
  "subparagraph-heading": 800,
});

export function jitterGap(baseMs: number, random: () => number): number {
  const sample = random();
  if (!Number.isFinite(baseMs) || baseMs < 0) {
    throw new RangeError("base gap must be non-negative");
  }
  if (!Number.isFinite(sample) || sample < 0 || sample > 1) {
    throw new RangeError("jitter source must return a value from 0 to 1");
  }
  return Math.round(baseMs * (0.9 + sample * 0.2));
}

export function createSpeechSchedule(
  segments: readonly SpeechSegment[],
  settings: ScheduleSettings,
  random: () => number,
): readonly ScheduledSpeech[] {
  const result: ScheduledSpeech[] = [];
  const scheduleSegment = (segment: SpeechSegment): ScheduledSpeech => {
    const markerBefore =
      segment.kind === "paragraph-heading" ||
      segment.kind === "subparagraph-heading";
    return {
      id: segment.id,
      kind: segment.kind,
      text: segment.text,
      zone: segment.zone,
      rate: rateForZone(settings.baseMultiplier, segment.zone),
      gapBeforeMs: jitterGap(BEFORE_GAPS[segment.kind], random),
      gapAfterMs: jitterGap(
        (HEADING_AFTER_GAPS[segment.kind] ?? 0) +
          (segment.zone === "critical" ? 600 : 0),
        random,
      ),
      markerBefore,
    };
  };

  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index];
    /* v8 ignore next -- bounded array iteration guarantees this invariant */
    if (segment === undefined) continue;
    if (segment.zone !== "mission") {
      result.push(scheduleSegment(segment));
      continue;
    }

    const mission: ScheduledSpeech[] = [];
    while (index < segments.length) {
      const missionSegment = segments[index];
      /* v8 ignore next -- index is bounded by the loop condition */
      if (missionSegment === undefined) break;
      if (missionSegment.zone !== "mission") break;
      mission.push(scheduleSegment(missionSegment));
      index += 1;
    }
    index -= 1;
    result.push(...mission);
    if (!(settings.repeatMission ?? true)) continue;
    result.push(
      ...mission.map((entry, missionIndex) => ({
        ...entry,
        id: `${entry.id}/repeat-02`,
        markerBefore: false,
        gapBeforeMs:
          missionIndex === 0 ? jitterGap(1500, random) : entry.gapBeforeMs,
      })),
    );
  }
  return result;
}

export interface ScheduleClock {
  wait(delayMs: number): Promise<void>;
}

export interface ScheduleSpeaker {
  cancel(): void;
  speak(request: {
    readonly rate: number;
    readonly text: string;
  }): Promise<void>;
}

export async function runSpeechSchedule(
  schedule: readonly ScheduledSpeech[],
  clock: ScheduleClock,
  speaker: ScheduleSpeaker,
  marker: () => Promise<void>,
): Promise<void> {
  speaker.cancel();
  for (const entry of schedule) {
    if (entry.gapBeforeMs > 0) await clock.wait(entry.gapBeforeMs);
    if (entry.markerBefore) await marker();
    await speaker.speak({ text: entry.text, rate: entry.rate });
    if (entry.gapAfterMs > 0) await clock.wait(entry.gapAfterMs);
  }
}
